/**
 * MaintenanceScreen.tsx — Schermata di manutenzione globale.
 *
 * Mostrata quando il backend risponde 503 DATABASE_UNAVAILABLE
 * (es. quota compute Neon esaurita): il server è vivo ma il DB è fermo.
 * La decisione di mostrarla vive in `api/core/maintenanceGuard.ts` (due
 * richieste distinte fallite + qualche secondo di grazia); gli eventi sono
 * `rideconnect:maintenance` (attivazione) / `rideconnect:maintenance-clear`
 * (ripristino automatico al primo 2xx).
 *
 * Il pulsante "Riprova" esegue un health-check diretto su /health/ready
 * (endpoint CON DB). Se ready risponde 200, la manutenzione è finita.
 *
 * AUTO-RECUPERO (30 Set 2026): la schermata verifica anche da sola ogni
 * AUTO_RETRY_MS, fino a MAX_AUTO_ATTEMPTS volte. Prima non c'era alcun
 * tentativo automatico: se il DB tornava disponibile, l'overlay restava a
 * schermo finché l'utente non toccava "Riprova ora" (o fino al successivo
 * 2xx di una chiamata API, che poteva arrivare 10 minuti dopo).
 * L'intervallo è volutamente ampio: /health/ready tenta una connessione al
 * DB e non deve tenere sveglio il compute Neon (vedi docs/NEON_QUOTA_ANALYSIS.md).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { resolveApiBaseUrl } from "@/utils/apiBase";

const API_BASE: string = resolveApiBaseUrl();

/** Intervallo tra i tentativi automatici (60s). */
export const AUTO_RETRY_MS = 60_000;
/** Tentativi automatici massimi (~10 minuti), poi solo manuale. */
export const MAX_AUTO_ATTEMPTS = 10;
/** Timeout della singola probe (cold start Render + query DB). */
export const PROBE_TIMEOUT_MS = 15_000;

export async function probeDatabaseReady(): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    // /health/ready fa una query al DB: 200 significa "tutto ok"
    const res = await fetch(`${API_BASE}/health/ready`, {
      signal: controller.signal,
      cache: "no-store",
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

export default function MaintenanceScreen() {
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);
  const attemptsRef = useRef(0);

  const runProbe = useCallback(async (): Promise<boolean> => {
    const ok = await probeDatabaseReady();
    if (ok) {
      window.dispatchEvent(new CustomEvent("rideconnect:maintenance-clear"));
    }
    return ok;
  }, []);

  const handleRetry = useCallback(async () => {
    // Un tentativo manuale azzera il contatore: l'utente resta in controllo.
    attemptsRef.current = 0;
    setChecking(true);
    setFailed(false);
    try {
      if (!(await runProbe())) setFailed(true);
    } finally {
      setChecking(false);
    }
  }, [runProbe]);

  // Auto-recupero: nessuna azione richiesta all'utente.
  useEffect(() => {
    const id = setInterval(() => {
      if (attemptsRef.current >= MAX_AUTO_ATTEMPTS) {
        clearInterval(id);
        return;
      }
      attemptsRef.current += 1;
      void runProbe();
    }, AUTO_RETRY_MS);
    return () => clearInterval(id);
  }, [runProbe]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-[#0f172a] text-white px-6 text-center"
      style={{
        paddingTop: "env(safe-area-inset-top, var(--safe-area-inset-top, 0px))",
        paddingBottom:
          "env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px))",
        paddingLeft:
          "env(safe-area-inset-left, var(--safe-area-inset-left, 0px))",
        paddingRight:
          "env(safe-area-inset-right, var(--safe-area-inset-right, 0px))",
      }}
    >
      <img
        src="/icon-512.png"
        alt="RideConnect"
        className="mb-6 h-24 w-24 select-none"
        draggable={false}
      />
      <h1 className="text-xl font-extrabold mb-3">
        RideConnect è in manutenzione
      </h1>
      <p className="text-neutral-400 text-sm max-w-xs mb-8 leading-relaxed">
        Riproviamo tra poco. Stiamo ripristinando il servizio: nessun dato è
        andato perso.
      </p>
      <button
        onClick={handleRetry}
        disabled={checking}
        className="px-6 py-3 rounded-2xl bg-[#FF6B35] text-white font-bold text-sm active:scale-95 transition-transform disabled:opacity-60"
      >
        {checking ? "Verifica in corso…" : "Riprova ora"}
      </button>
      {failed && (
        <p className="mt-4 text-xs text-amber-400 max-w-xs">
          Il servizio non è ancora disponibile. Riprova tra qualche minuto.
        </p>
      )}
      <p className="mt-6 text-[11px] text-neutral-500 max-w-xs">
        Verifica automatica ogni minuto: la schermata si chiude da sola appena
        il servizio torna disponibile.
      </p>
    </div>
  );
}
